'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabaseClient';

const CONTACT_ATTACHMENTS_BUCKET = 'contact-inquiries';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  service: string;
  message: string;
}

interface FormErrors {
  [key: string]: string;
}

const serviceOptions = [
  'Website Development',
  'Mobile App Development',
  'Full Stack Development',
  'Cloud Solutions',
  'Cybersecurity',
  'UI/UX Design',
  'Software Testing',
  'Multiple Services',
  'Not Sure - Need Consultation',
];

export default function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [requirementFile, setRequirementFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedPhone = formData.phone.trim();

    if (!trimmedName) {
      newErrors.name = 'Full name is required.';
    } else if (!/^[A-Za-z][A-Za-z\s.'-]{1,79}$/.test(trimmedName)) {
      newErrors.name = 'Please enter a valid full name.';
    }

    if (!trimmedEmail) {
      newErrors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      newErrors.email = 'Please enter a valid email address.';
    }

    if (!trimmedPhone) {
      newErrors.phone = 'Mobile number is required.';
    } else if (!/^\d{10}$/.test(trimmedPhone)) {
      newErrors.phone = 'Please enter a valid 10-digit mobile number.';
    }
    if (!formData.service) newErrors.service = 'Please select a service.';
    if (!formData.message.trim()) newErrors.message = 'Please describe your project.';
    else if (formData.message.trim().length < 20) newErrors.message = 'Please provide at least 20 characters.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    const allowedExtensions = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'txt'];

    if (!file) {
      setRequirementFile(null);
      setErrors((prev) => ({ ...prev, requirementFile: '' }));
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!allowedExtensions.includes(extension)) {
      setErrors((prev) => ({
        ...prev,
        requirementFile:
          'Please upload a PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, or TXT file.',
      }));
      e.target.value = '';
      setRequirementFile(null);
      return;
    }

    setRequirementFile(file);
    setErrors((prev) => ({ ...prev, requirementFile: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('submitting');
    setErrors((prev) => ({ ...prev, submit: '' }));

    try {
      let attachmentPath: string | null = null;

      if (requirementFile) {
        const safeName = requirementFile.name.replace(/[^a-zA-Z0-9.]/g, '_');
        const path = `inquiries/${Date.now()}-${safeName}`;

        const { error: uploadError } = await supabase.storage
          .from(CONTACT_ATTACHMENTS_BUCKET)
          .upload(path, requirementFile);

        if (uploadError) throw uploadError;
        attachmentPath = path;
      }

      const { error: insertError } = await supabase.from('contact_inquiries').insert({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        company: formData.company.trim() || null,
        service: formData.service,
        message: formData.message.trim(),
        attachment_bucket: attachmentPath ? CONTACT_ATTACHMENTS_BUCKET : null,
        attachment_path: attachmentPath,
      });

      if (insertError) throw insertError;

      setStatus('success');
    } catch (err) {
      console.error('Error submitting contact form to Supabase:', err);
      setStatus('error');
      setErrors((prev) => ({
        ...prev,
        submit:
          'Something went wrong while sending your inquiry. Please try again in a moment.',
      }));
    }
  };

  const inputClass = (field: string) =>
    `w-full px-4 py-3.5 bg-background-muted border rounded-xl font-body text-body-base text-foreground placeholder:text-foreground-muted focus:placeholder-transparent transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${
      errors[field] ? 'border-error bg-red-50' : 'border-border hover:border-primary/30'
    }`;

  if (status === 'success') {
    return (
      <div className="bg-background-card rounded-3xl border border-border shadow-lg-card p-6 sm:p-12 text-center">
        <div className="w-20 h-20 bg-secondary/10 rounded-3xl flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircleIcon" size={40} className="text-success" />
        </div>
        <h3 className="font-heading font-800 text-display-sm text-foreground mb-3">
          Message Received!
        </h3>
        <p className="font-body text-body-lg text-foreground-secondary mb-6 max-w-md mx-auto">
          Thank you, <strong>{formData.name}</strong>. Our team will review your inquiry and 
          reach out within 24 business hours.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => {
              setStatus('idle');
              setFormData({
                name: '',
                email: '',
                phone: '',
                company: '',
                service: '',
                message: '',
              });
              setRequirementFile(null);
            }}
            className="px-6 py-3 bg-background-muted border border-border text-foreground font-heading font-600 text-body-sm rounded-xl hover:bg-primary-50 hover:border-primary/30 transition-all duration-200"
          >
            Submit Another Inquiry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div id="contact-form" className="relative scroll-mt-28">
      <div className="pointer-events-none absolute inset-0 translate-x-[6px] translate-y-[6px] rounded-3xl border border-slate-300/70 bg-[linear-gradient(145deg,rgba(186,194,205,0.96)_0%,rgba(205,213,223,0.94)_45%,rgba(221,227,235,0.92)_100%)]" />
      {/* Form Header */}
      <div className="absolute inset-0 overflow-hidden rounded-3xl border border-white/55 bg-[linear-gradient(145deg,rgba(255,255,255,0.88)_0%,rgba(242,246,250,0.82)_38%,rgba(226,233,242,0.84)_100%)] backdrop-blur-xl shadow-[1px_1px_3px_rgba(15,23,42,0.07),2px_3px_5px_rgba(15,23,42,0.05),inset_0_1px_0_rgba(255,255,255,1),inset_0_-1px_0_rgba(255,255,255,0.28)]">
        <div className="pointer-events-none absolute inset-0 rounded-3xl bg-[radial-gradient(circle_at_18%_16%,rgba(255,255,255,0.42),transparent_30%),radial-gradient(circle_at_84%_22%,rgba(255,255,255,0.18),transparent_28%)]" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-[linear-gradient(180deg,rgba(255,255,255,0.46),transparent)]" />
        <div className="pointer-events-none absolute -left-6 top-[-12%] h-[150%] w-24 rotate-[28deg] bg-[linear-gradient(180deg,rgba(255,255,255,0.72)_0%,rgba(255,255,255,0.18)_45%,rgba(255,255,255,0.03)_100%)] opacity-90" />
        <div className="pointer-events-none absolute right-7 top-[-20%] h-[138%] w-[2px] rotate-[28deg] bg-white/95 opacity-95 shadow-[0_0_10px_rgba(255,255,255,0.8)]" />
        <div className="pointer-events-none absolute left-10 top-[8%] h-[2px] w-[78%] rotate-[28deg] bg-white/85 opacity-85 shadow-[0_0_10px_rgba(255,255,255,0.65)]" />
      </div>
      <div className="relative p-5 sm:p-6">
        <div className="relative z-10">
        <p className="font-body text-caption text-primary uppercase  mb-2">
          Business Inquiry
        </p>
        <h2 className="font-heading font-800 text-display-sm text-foreground mb-2">
          Tell Us About Your Business Requirement
        </h2>
        <p className="font-body text-body-base text-foreground-secondary leading-relaxed">
          Fill out the form below and we&apos;ll prepare a practical response with the right next step for your business requirement.
        </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="relative z-10 -mt-2 sm:-mt-3 mx-0 p-5 sm:p-6 space-y-4 rounded-3xl border border-border bg-white shadow-lg-card"
        noValidate
      >
        {/* Row 1: Name + Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block font-heading font-700 text-body-sm text-foreground mb-2">
              Full Name <span className="text-error">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={handleChange}
              className={inputClass('name')}
              aria-describedby={errors.name ? 'name-error' : undefined}
              aria-invalid={!!errors.name}
            />
            {errors.name && (
              <p id="name-error" className="mt-1.5 font-body text-caption text-error flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} />
                {errors.name}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block font-heading font-700 text-body-sm text-foreground mb-2">
              Email Address <span className="text-error">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleChange}
              className={inputClass('email')}
              aria-describedby={errors.email ? 'email-error' : undefined}
              aria-invalid={!!errors.email}
            />
            {errors.email && (
              <p id="email-error" className="mt-1.5 font-body text-caption text-error flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} />
                {errors.email}
              </p>
            )}
          </div>
        </div>

        {/* Row 2: Phone + Company */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block font-heading font-700 text-body-sm text-foreground mb-2">
              Phone Number <span className="text-error">*</span>
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              inputMode="numeric"
              pattern="[0-9]{10}"
              maxLength={10}
              placeholder="Enter your phone number"
              value={formData.phone}
              onChange={handleChange}
              className={inputClass('phone')}
              aria-describedby={errors.phone ? 'phone-error' : undefined}
              aria-invalid={!!errors.phone}
            />
            {errors.phone && (
              <p id="phone-error" className="mt-1.5 font-body text-caption text-error flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} />
                {errors.phone}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="company" className="block font-heading font-700 text-body-sm text-foreground mb-2">
              Company / Organization (Optional)
            </label>
            <input
              id="company"
              name="company"
              type="text"
              autoComplete="organization"
              placeholder="Enter your company name"
              value={formData.company}
              onChange={handleChange}
              className={inputClass('company')}
            />
          </div>
        </div>

        {/* Row 3: Service + Requirement Document */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="service" className="block font-heading font-700 text-body-sm text-foreground mb-2">
              Service Needed <span className="text-error">*</span>
            </label>
            <div className="relative">
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                className={`${inputClass('service')} cursor-pointer appearance-none pr-10`}
                aria-describedby={errors.service ? 'service-error' : undefined}
                aria-invalid={!!errors.service}
              >
                <option value="">Select a service...</option>
                {serviceOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <Icon
                name="ChevronDownIcon"
                size={16}
                className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-foreground-muted"
              />
            </div>
            {errors.service && (
              <p id="service-error" className="mt-1.5 font-body text-caption text-error flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} />
                {errors.service}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="requirementFile" className="block font-heading font-700 text-body-sm text-foreground mb-2">
              Attach Requirement Document (Optional)
            </label>
            <input
              id="requirementFile"
              name="requirementFile"
              type="file"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
              onChange={handleFileChange}
              className="block w-full font-body text-body-sm font-400 text-foreground file:mr-4 file:py-2.5 file:px-5 file:rounded-xl file:border-0 file:font-heading file:text-body-sm file:font-600 file:bg-gradient-primary file:text-white cursor-pointer"
            />
            <p className="mt-1.5 font-body text-body-sm text-foreground-muted leading-relaxed">
              Upload format PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, or TXT format.
            </p>
            {errors.requirementFile && (
              <p className="mt-1.5 font-body text-caption text-error flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} />
                {errors.requirementFile}
              </p>
            )}
          </div>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block font-heading font-700 text-body-sm text-foreground mb-2">
            Project Description <span className="text-error">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            rows={5}
            placeholder="Describe your project goals, current challenges, and requirements."
            value={formData.message}
            onChange={handleChange}
            className={`${inputClass('message')} resize-none`}
            aria-describedby={errors.message ? 'message-error' : undefined}
            aria-invalid={!!errors.message}
          />
          <div className="flex items-start sm:items-center justify-between mt-1.5 gap-2">
            {errors.message ? (
              <p id="message-error" className="font-body text-caption text-error flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} />
                {errors.message}
              </p>
            ) : (
              <span />
            )}
            <span className="font-body text-caption text-foreground-muted">
              {formData.message.length} chars
            </span>
          </div>
        </div>

        {/* Privacy Note */}
        <p className="font-body text-body-base text-foreground-muted leading-relaxed">
          By submitting this form, you agree to our{' '}
          <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>. 
          Your information is never shared with third parties. We'll sign an NDA on request.
        </p>

        {/* Submit */}
        {errors.submit && (
          <p className="font-body text-caption text-error flex items-center gap-1 mb-2">
            <Icon name="ExclamationCircleIcon" size={12} />
            {errors.submit}
          </p>
        )}
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full flex items-center justify-center gap-3 py-4 bg-gradient-primary text-white font-heading font-700 text-body-base rounded-2xl shadow-[1px_1px_3px_rgba(15,23,42,0.07),2px_3px_5px_rgba(15,23,42,0.05)] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          {status === 'submitting' ? (
            <>
              <svg className="animate-spin w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Sending Your Inquiry...
            </>
          ) : (
            <>
              Send Project Inquiry
              <Icon name="PaperAirplaneIcon" size={18} />
            </>
          )}
        </button>
      </form>
    </div>
  );
}

